import { useEffect, useMemo, useState } from 'react';
import { AppSelect } from '@/components/shared/AppSelect';
import { Badge } from '@/components/shared/Badge';
import { Button } from '@/components/shared/Button';
import { Panel } from '@/components/shared/Panel';
import { appRoleOptions, approvalStatusOptions } from '@/data/options';
import type { DiscordTestDmResult, ProfileAccessPayload } from '@/lib/repositories/adminRepository';
import type { AppRole, Profile } from '@/types/domain';
import { formatDateTime } from '@/utils/date';
import { getProfileInGameName } from '@/utils/profile';
import { formatAppRoleLabel, getPrimaryRole, rolePriority } from '@/utils/roles';

interface UserManagementPanelProps {
  users: Profile[];
  currentProfileId?: string;
  isAdmin: boolean;
  onSave: (payload: ProfileAccessPayload) => Promise<void>;
  onSetGuildMembershipActive: (profileId: string, isActive: boolean) => Promise<void>;
  onSendTestDm: (profileId?: string) => Promise<DiscordTestDmResult>;
}

const DISCORD_BOT_INVITE_URL =
  'https://discord.com/oauth2/authorize?client_id=1491069597909127239&scope=bot%20applications.commands&permissions=0';

function sortUsers(users: Profile[]): Profile[] {
  return [...users].sort((left, right) => {
    if (left.approvalStatus === 'pending' && right.approvalStatus !== 'pending') {
      return -1;
    }

    if (left.approvalStatus !== 'pending' && right.approvalStatus === 'pending') {
      return 1;
    }

    return left.displayName.localeCompare(right.displayName);
  });
}

function formatRoleList(roles: AppRole[]): string {
  return [...roles]
    .sort((left, right) => rolePriority.indexOf(left) - rolePriority.indexOf(right))
    .map((role) => formatAppRoleLabel(role))
    .join(', ');
}

function formatDiscordDmMessage(message: string | null | undefined): string | null {
  if (!message?.trim()) {
    return null;
  }

  if (/no mutual guilds/i.test(message)) {
    return 'The Woof Woof bot is not in the same Discord server as this member yet.';
  }

  return message;
}

export function UserManagementPanel({
  users,
  currentProfileId,
  isAdmin,
  onSave,
  onSetGuildMembershipActive,
  onSendTestDm,
}: UserManagementPanelProps): JSX.Element {
  const sortedUsers = useMemo(() => sortUsers(users), [users]);
  const [selectedProfileId, setSelectedProfileId] = useState(sortedUsers[0]?.id ?? '');
  const selectedUser = sortedUsers.find((user) => user.id === selectedProfileId) ?? sortedUsers[0] ?? null;
  const [approvalStatus, setApprovalStatus] = useState(selectedUser?.approvalStatus ?? 'pending');
  const [inGameName, setInGameName] = useState(selectedUser?.inGameName ?? '');
  const [notes, setNotes] = useState(selectedUser?.notes ?? '');
  const [roles, setRoles] = useState<AppRole[]>(selectedUser?.roles ?? ['member']);
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingTestDm, setIsSendingTestDm] = useState(false);
  const [testDmMessage, setTestDmMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!sortedUsers.some((user) => user.id === selectedProfileId)) {
      setSelectedProfileId(sortedUsers[0]?.id ?? '');
    }
  }, [selectedProfileId, sortedUsers]);

  useEffect(() => {
    setApprovalStatus(selectedUser?.approvalStatus ?? 'pending');
    setInGameName(selectedUser?.inGameName ?? '');
    setNotes(selectedUser?.notes ?? '');
    setRoles(selectedUser?.roles ?? ['member']);
    setTestDmMessage(null);
  }, [selectedUser]);

  const selectedUserHasProtectedRoles = Boolean(selectedUser?.roles.some((role) => role === 'owner' || role === 'admin' || role === 'officer'));
  const isReadOnlyTarget = !isAdmin && selectedUserHasProtectedRoles;
  const roleList = selectedUser ? formatRoleList(selectedUser.roles) : '';
  const primaryRoleLabel = selectedUser ? formatAppRoleLabel(getPrimaryRole(selectedUser.roles)) : '';
  const discordStatusMessage = formatDiscordDmMessage(selectedUser?.dmLastSentAt ? `Last sent ${formatDateTime(selectedUser.dmLastSentAt)}` : selectedUser?.dmLastError ? `Last error: ${selectedUser.dmLastError}` : 'No DM activity logged yet.');
  const formattedTestDmMessage = formatDiscordDmMessage(testDmMessage);
  const shouldShowInviteBot =
    /no mutual guilds/i.test(selectedUser?.dmLastError ?? '') || /no mutual guilds/i.test(testDmMessage ?? '');

  return (
    <Panel
      title="Guild User Management"
      description="Approve pending members, keep in-game names accurate, and control guild portal permissions from the live roster."
    >
      <div className="management-layout">
        <div className="management-list">
          {sortedUsers.map((user) => (
            <button
              key={user.id}
              type="button"
              className={user.id === selectedUser?.id ? 'management-list__item management-list__item--active' : 'management-list__item'}
              onClick={() => setSelectedProfileId(user.id)}
            >
              <div>
                <strong>{user.displayName}</strong>
                <span>{getProfileInGameName(user)}</span>
              </div>
              <div className="management-list__meta">
                <Badge tone={user.approvalStatus === 'approved' ? 'success' : user.approvalStatus === 'pending' ? 'warning' : 'default'}>
                  {user.approvalStatus}
                </Badge>
                <span>{formatRoleList(user.roles)}</span>
              </div>
            </button>
          ))}
        </div>

        {selectedUser ? (
          <div className="management-editor">
            <div className="profile-summary">
              <div>
                <span className="profile-summary__label">Discord Name</span>
                <strong>{selectedUser.displayName}</strong>
                <span className="profile-summary__hint">@{selectedUser.discordUsername}</span>
              </div>
              <div>
                <span className="profile-summary__label">Guild Rank</span>
                <strong>{primaryRoleLabel}</strong>
                {roleList && roleList !== primaryRoleLabel ? <span className="profile-summary__hint">{roleList}</span> : null}
              </div>
              <div>
                <span className="profile-summary__label">Joined Portal</span>
                <strong>{formatDateTime(selectedUser.createdAt)}</strong>
                {selectedUser.id === currentProfileId ? <span className="profile-summary__hint">This is your account.</span> : null}
              </div>
              <div>
                <span className="profile-summary__label">Discord DMs</span>
                <strong>
                  {!selectedUser.discordId
                    ? 'Discord missing'
                    : selectedUser.guildMembershipActive === false
                      ? 'Guild inactive'
                    : selectedUser.dmNotificationsPaused
                      ? 'Paused'
                      : selectedUser.dmMassAlertsEnabled
                        ? 'Mass alerts on'
                        : 'Disabled'}
                </strong>
                {discordStatusMessage ? <span className="profile-summary__hint">{discordStatusMessage}</span> : null}
              </div>
              <div>
                <span className="profile-summary__label">Guild Membership</span>
                <strong>{selectedUser.guildMembershipActive === false ? 'Inactive' : 'Active'}</strong>
                {selectedUser.guildMembershipActive === false ? (
                  <span className="profile-summary__hint">Blocked from guild Discord DMs until staff restores membership.</span>
                ) : null}
              </div>
            </div>

            <div className="form-actions form-actions--start">
              {isAdmin ? (
                <Button
                  variant="secondary"
                  disabled={isSendingTestDm || selectedUser.approvalStatus !== 'approved' || selectedUser.guildMembershipActive === false}
                  onClick={async () => {
                    if (!selectedUser) {
                      return;
                    }

                    setIsSendingTestDm(true);
                    setTestDmMessage(null);

                    try {
                      const result = await onSendTestDm(selectedUser.id);
                      setTestDmMessage(result.message);
                    } catch (error) {
                      setTestDmMessage(error instanceof Error ? error.message : 'Failed to send the Discord test DM.');
                    } finally {
                      setIsSendingTestDm(false);
                    }
                  }}
                >
                  {isSendingTestDm ? 'Sending Test DM...' : 'Send Test DM'}
                </Button>
              ) : null}
              {shouldShowInviteBot ? (
                <a
                  className="button button--secondary"
                  href={DISCORD_BOT_INVITE_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  Invite Discord Bot
                </a>
              ) : null}
              <Button
                variant={selectedUser.guildMembershipActive === false ? 'secondary' : 'danger'}
                disabled={isSendingTestDm || isReadOnlyTarget}
                onClick={async () => {
                  if (!selectedUser) {
                    return;
                  }

                  setIsSendingTestDm(true);
                  setTestDmMessage(null);

                  try {
                    const nextIsActive = selectedUser.guildMembershipActive === false;
                    await onSetGuildMembershipActive(selectedUser.id, nextIsActive);
                    setTestDmMessage(nextIsActive ? 'Guild membership restored for Discord DMs.' : 'Guild membership marked inactive for Discord DMs.');
                  } catch (error) {
                    setTestDmMessage(error instanceof Error ? error.message : 'Failed to update guild membership for Discord DMs.');
                  } finally {
                    setIsSendingTestDm(false);
                  }
                }}
              >
                {selectedUser.guildMembershipActive === false ? 'Restore Guild Member' : 'Mark Left Guild'}
              </Button>
              {formattedTestDmMessage ? <span className="form-footnote">{formattedTestDmMessage}</span> : null}
            </div>

            <div className="form-grid form-grid--two">
              <label className="field">
                <span className="field__label">Approval Status</span>
                <AppSelect
                  ariaLabel="Approval status"
                  value={approvalStatus}
                  disabled={isReadOnlyTarget}
                  onChange={(value) => setApprovalStatus(value as typeof approvalStatus)}
                  options={approvalStatusOptions.map((status) => ({
                    value: status,
                    label: status[0].toUpperCase() + status.slice(1),
                    description: 'Guild access state',
                  }))}
                />
              </label>

              <label className="field">
                <span className="field__label">In-Game Name</span>
                <input
                  className="field__input"
                  value={inGameName}
                  disabled={isReadOnlyTarget}
                  onChange={(event) => setInGameName(event.target.value)}
                />
              </label>
            </div>

            <label className="field">
              <span className="field__label">Notes</span>
              <textarea
                className="field__input field__input--textarea"
                value={notes}
                disabled={isReadOnlyTarget}
                onChange={(event) => setNotes(event.target.value)}
              />
            </label>

            <div className="role-picker">
              {appRoleOptions.map((role) => {
                const isProtectedRole = role === 'owner' || role === 'admin' || role === 'officer';
                const isDisabled = role === 'member' || (!isAdmin && isProtectedRole) || (!isAdmin && selectedUserHasProtectedRoles);

                return (
                  <label key={role} className={isDisabled ? 'role-pill role-pill--disabled' : 'role-pill'}>
                    <input
                      type="checkbox"
                      checked={roles.includes(role)}
                      disabled={isDisabled}
                      onChange={(event) => {
                        setRoles((currentRoles) => {
                          const nextRoles = event.target.checked
                            ? [...currentRoles, role]
                            : currentRoles.filter((entry) => entry !== role);

                          return nextRoles.includes('member') ? nextRoles : [...nextRoles, 'member'];
                        });
                      }}
                    />
                    <span>{formatAppRoleLabel(role)}</span>
                  </label>
                );
              })}
            </div>

            {!isAdmin ? (
              <p className="form-footnote">
                Officers can manage approvals, notes, in-game names, and mass-manager/member access. Admin and officer role changes stay admin-only.
              </p>
            ) : null}

            {isReadOnlyTarget ? (
              <p className="form-footnote">Only admins can modify another officer or admin account.</p>
            ) : null}

            <div className="form-actions">
              <Button
                variant="secondary"
                onClick={async () => {
                  if (!selectedUser) {
                    return;
                  }

                  setIsSaving(true);

                  try {
                    await onSave({
                      profileId: selectedUser.id,
                      approvalStatus: 'approved',
                      inGameName,
                      notes,
                      roles,
                    });
                  } finally {
                    setIsSaving(false);
                  }
                }}
                disabled={isSaving || isReadOnlyTarget}
              >
                Quick Approve
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  if (!selectedUser) {
                    return;
                  }

                  setIsSaving(true);

                  try {
                    await onSave({
                      profileId: selectedUser.id,
                      approvalStatus: 'rejected',
                      inGameName,
                      notes,
                      roles,
                    });
                  } finally {
                    setIsSaving(false);
                  }
                }}
                disabled={isSaving || isReadOnlyTarget}
              >
                Reject
              </Button>
              <Button
                onClick={async () => {
                  if (!selectedUser) {
                    return;
                  }

                  setIsSaving(true);

                  try {
                    await onSave({
                      profileId: selectedUser.id,
                      approvalStatus,
                      inGameName,
                      notes,
                      roles,
                    });
                  } finally {
                    setIsSaving(false);
                  }
                }}
                disabled={isSaving || isReadOnlyTarget}
              >
                {isSaving ? 'Saving...' : 'Save User Changes'}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
